/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { AutocompleteLoadMoreEvent, KEY_DOWN, KEY_UP } from '@gh/core-ui';
import { Column, ICellEditorParams, IRowNode } from 'ag-grid-community';
import { defer, includes, isFunction, isString, property } from 'lodash';
import { CellParams } from '../../../state';
import { EditableCellModel } from '../../../state/grid-state.interfaces';
import { createCellFormControl } from '../../grid-cell.utils';
import { GridCellEditorComponent } from '../../grid-component.interfaces';

const STOPPED_KEYS = [KEY_DOWN, KEY_UP];

export interface GridAutocompleteCellConfig {
  idProperty?: string;
  nameProperty: string | TransformFn<any, string>;
  displayProperty?: string;
  isGroup?: PredicateFn<any>;
  highlightedProperty?: string | TransformFn<any, boolean>;

  loadMore(event: AutocompleteLoadMoreEvent, params: CellParams): void;
}

@Component({
  standalone: false,
  selector: 'gh-grid-autocomplete-cell-editor',
  templateUrl: 'grid-autocomplete-cell-editor.component.html',
  styleUrls: ['grid-autocomplete-cell-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridAutocompleteCellEditorComponent implements GridCellEditorComponent, AfterViewInit, OnDestroy {
  required: boolean = false;
  control: FormControl;

  @ViewChild('input', { static: false }) inputEl: ElementRef;

  config: GridAutocompleteCellConfig;
  _textKey: TransformFn<any, string> = property('text');

  private stopEditing: () => any;
  private focusTimeout: number;

  private node: IRowNode;
  private column: Column;

  get cellParams(): CellParams {
    const { node, column } = this;

    return {
      node,
      colDef: column.getColDef(),
      data: node.data,
    };
  }

  ngAfterViewInit(): void {
    this.focusTimeout = defer(() => {
      this.inputEl.nativeElement.focus();
      this.inputEl.nativeElement.select();
      this.focusTimeout = 0;
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.focusTimeout);
  }

  agInit(params: ICellEditorParams): void {
    const model: EditableCellModel = params['modelFactory']({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    this.node = params.node;
    this.column = params.column;
    this.required = model.required;
    this.control = createCellFormControl(model);
    this.config = params['config'];
    this._textKey = isString(this.config.nameProperty) ? property(this.config.nameProperty) : this.config.nameProperty;
    this.stopEditing = params.stopEditing;
  }

  isHighlighted(item: any): boolean {
    if (this.config.highlightedProperty) {
      if (isFunction(this.config.highlightedProperty)) {
        return this.config.highlightedProperty(item);
      } else if (isString(this.config.highlightedProperty)) {
        return item[this.config.highlightedProperty];
      }
    }
    return false;
  }

  getValue(): any {
    return this.control.value;
  }

  onSelect(): void {
    this.stopEditing();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (includes(STOPPED_KEYS, event.keyCode)) {
      event.stopPropagation();
    }
  }
}
